import { Link } from '@tanstack/react-router';
import { FileTextIcon } from '@phosphor-icons/react';
import { badgeVariants } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getClause } from '../data/clauses';

interface ClauseChipProps {
  clauseId: string;
  /** Show the extracted value after the section number. */
  showValue?: boolean;
}

/**
 * Small pill that cites a clause ("§4.2 · Net 45") and links to the contract
 * page with that clause highlighted. Hover shows the contract id and quote.
 */
export function ClauseChip({ clauseId, showValue = true }: ClauseChipProps) {
  const clause = getClause(clauseId);
  if (!clause) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/procurement/contracts/$contractId"
          params={{ contractId: clause.contractId }}
          search={{ clause: clause.id }}
          className={cn(
            badgeVariants({ variant: 'outline' }),
            'max-w-full gap-1 bg-white font-medium text-stone-700 hover:bg-stone-100',
          )}
        >
          <FileTextIcon className="size-3 shrink-0" aria-hidden />
          <span className="tabular-nums">§{clause.section}</span>
          {showValue ? (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{clause.value}</span>
            </>
          ) : null}
        </Link>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">
        <p className="font-mono tabular-nums">
          {clause.contractId} §{clause.section}
        </p>
        <p className="mt-1 line-clamp-6">“{clause.quote}”</p>
      </TooltipContent>
    </Tooltip>
  );
}
